import { uiText, useUiLanguage, currentLocale } from '@/i18n/interface';

import React from 'react';
import { CheckCircle, Circle } from 'lucide-react';

interface Requirement {
  id: string;
  requirement_name: string;
  completed: boolean;
  completed_date: string | null;
  notes: string | null;
  category?: string;
  description?: string;
  title?: string;
  points?: number;
  progress_percentage?: number;
  points_earned?: number;
}

interface RequirementItemProps {
  requirement: Requirement | null | undefined;
  onToggle?: (id: string) => void;
}

const RequirementItem: React.FC<RequirementItemProps> = ({ requirement, onToggle }) => {
  useUiLanguage();
  // Early return if requirement is null/undefined
  if (!requirement) {
    console.warn('RequirementItem: requirement is null or undefined');
    return null;
  }

  // Validate required fields
  if (!requirement.id) {
    console.warn('RequirementItem: requirement missing id:', requirement);
    return null;
  }

  const handleClick = () => {
    if (onToggle && requirement.id) {
      try {
        onToggle(requirement.id);
      } catch (error) {
        console.error('Error in handleClick:', error);
      }
    }
  };

  // Safely extract properties with fallbacks
  const requirementName = requirement.requirement_name || requirement.title || 'Unnamed Requirement';
  const isCompleted = Boolean(requirement.completed);
  const completedDate = requirement.completed_date;
  const notes = requirement.notes;
  const description = requirement.description;

  return (
    <div
      className={`flex items-start gap-3 p-3 bg-gray-50 rounded-lg ${onToggle ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''}`}
      onClick={handleClick}
      role={onToggle ? "button" : "listitem"}
      tabIndex={onToggle ? 0 : -1}
    >
      <div className="flex-shrink-0 mt-1">
        {isCompleted ? (
          <CheckCircle className="h-5 w-5 text-green-600" />
        ) : (
          <Circle className="h-5 w-5 text-gray-400" />
        )}
      </div>
      <div className="flex-1">
        <h4 className={`font-medium ${isCompleted ? 'text-green-700 line-through' : 'text-gray-900'}`}>
          {requirementName}
        </h4>
        {isCompleted && completedDate && (
          <p className="text-sm text-green-600 mt-1">
            {uiText("Completed on")}{' '}{new Date(completedDate).toLocaleDateString(currentLocale())}
          </p>
        )}
        {notes && (
          <p className="text-sm text-gray-600 mt-1">{notes}</p>
        )}
        {description && (
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        )}
      </div>
    </div>
  );
};

export default RequirementItem;
